/**
 * Entrance dots: hidden → grid cells (T-1→T0) → world morph in world space (T0→T1).
 * Each grid cell stacks DOTS_PER_GRID_CELL world points that share its screen position at morph.
 */
import * as pc from 'playcanvas';
import {
  DOTS_PER_GRID_CELL,
  ENTRANCE_DOT_COUNT,
  GRID_CELL_COUNT,
} from '@viewer/effects/subsamplePcv';
import {
  assignLogoTargetsToWorldTargets,
  fitLogoRect,
  logoPointToScreenPoint,
  type LogoFormation,
  type ScreenPoint,
} from '@viewer/playcanvas/entranceDotChoreography';
import {
  logoEntryOffsetY,
  sceneWaitPulseOffset,
} from '@viewer/playcanvas/entranceMotion';

const GRID_SPACING_PX = 22;
const GRID_SPAWN_DURATION_MS = 5000;
const LOGO_SPAWN_DURATION_MS = 2200;
const MORPH_DURATION_S = 2.8;
const SPAWN_DEPTH = 12;

export type PointDotsPhase = 'hidden' | 'grid-spawn' | 'grid-hold' | 'morphing' | 'done';

export type PcvToWorldFn = (local: pc.Vec3, out: pc.Vec3) => void;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

interface GridCell {
  sx: number;
  sy: number;
  ring: number;
}

function buildGridCells(canvasW: number, canvasH: number): { cells: GridCell[]; maxRing: number } {
  const cols = Math.max(1, Math.ceil(canvasW / GRID_SPACING_PX));
  const rows = Math.max(1, Math.ceil(canvasH / GRID_SPACING_PX));
  const offsetX = (canvasW - (cols - 1) * GRID_SPACING_PX) * 0.5;
  const offsetY = (canvasH - (rows - 1) * GRID_SPACING_PX) * 0.5;
  const centerCol = Math.floor((cols - 1) / 2);
  const centerRow = Math.floor((rows - 1) / 2);
  const maxRing = Math.ceil(Math.max(cols, rows) / 2);

  const cells: GridCell[] = [];
  for (let ring = 0; ring <= maxRing; ring++) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const dist = Math.max(Math.abs(col - centerCol), Math.abs(row - centerRow));
        if (dist !== ring) continue;
        cells.push({
          sx: offsetX + col * GRID_SPACING_PX,
          sy: offsetY + row * GRID_SPACING_PX,
          ring,
        });
      }
    }
  }
  return { cells, maxRing };
}

export class PlayCanvasPointDots {
  readonly entity: pc.Entity;
  private mesh: pc.Mesh;
  private material: pc.StandardMaterial;
  private readonly positions: Float32Array;
  /** PCV targets in splat-local space. */
  private readonly pcvTargets: Float32Array;
  /** Formation starts in camera-local space (length ENTRANCE_DOT_COUNT). */
  private readonly formationStartsCam: Float32Array;
  private readonly logoScreenPoints: ScreenPoint[] | null;
  /** World-space lerp endpoints after morph begins (length ENTRANCE_DOT_COUNT). */
  private readonly morphStartWorld: Float32Array;
  private readonly morphEndWorld: Float32Array;
  private readonly cellRingVisible: Uint32Array;
  private readonly maxRing: number;
  private readonly ringIntervalMs: number;
  private readonly canvasH: number;
  private readonly cameraEntity: pc.Entity;
  private readonly sceneRoot: pc.Entity;
  private currentRing = -1;
  private visibleDots = 0;
  private drawBase = 0;
  private drawCount = 0;
  private phase: PointDotsPhase = 'hidden';
  private morphT = 0;
  private lastRingMs = 0;
  private spawnStartedMs = 0;
  private sceneHoldStartedMs = 0;
  private readonly scratch = new pc.Vec3();
  private readonly scratch2 = new pc.Vec3();
  private readonly camMat = new pc.Mat4();
  private readonly invCam = new pc.Mat4();

  /** World-space origin of the radial reveal wave (matches GsplatRevealRadial center). */
  private revealCenterWorld: pc.Vec3 | null = null;
  /**
   * Per-dot distance from {@link revealCenterWorld} at its final silhouette position,
   * sorted ascending. After the morph, dots are reordered to match this so the dissolve
   * is a single contiguous slice (cheap, no per-frame sorting).
   */
  private sortedRadii: Float32Array | null = null;
  /** Number of inner dots already converted to splats (culled) by the passing wave. */
  private dissolvedCount = 0;

  constructor(
    device: pc.GraphicsDevice,
    pcvPositions: Float32Array,
    pcvPointCount: number,
    cameraEntity: pc.Entity,
    sceneRoot: pc.Entity,
    canvasW: number,
    canvasH: number,
    logoFormation?: LogoFormation | null,
  ) {
    this.cameraEntity = cameraEntity;
    this.sceneRoot = sceneRoot;
    this.canvasH = canvasH;
    this.pcvTargets = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    this.formationStartsCam = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    this.morphStartWorld = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    this.morphEndWorld = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    this.positions = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    this.logoScreenPoints =
      logoFormation?.points && logoFormation.points.length > 0 ? logoFormation.points : null;

    for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      const src = Math.min(pcvPointCount - 1, i);
      const s3 = src * 3;
      this.pcvTargets[i3] = pcvPositions[s3]!;
      this.pcvTargets[i3 + 1] = pcvPositions[s3 + 1]!;
      this.pcvTargets[i3 + 2] = pcvPositions[s3 + 2]!;
    }

    const cam = cameraEntity.camera!;
    this.invCam.copy(cameraEntity.getWorldTransform()).invert();
    if (this.logoScreenPoints) {
      this.maxRing = 1;
      this.cellRingVisible = new Uint32Array([0, GRID_CELL_COUNT]);
      const aspect =
        logoFormation?.aspectRatio && logoFormation.aspectRatio > 0 ? logoFormation.aspectRatio : 4;
      const maxBox = {
        width: Math.min(canvasW * 0.62, canvasH * 1.7),
        height: canvasH * 0.24,
      };
      const fitted = fitLogoRect(maxBox, aspect);
      const rect = {
        left: (canvasW - maxBox.width) * 0.5 + fitted.left,
        top: canvasH * 0.44 - maxBox.height * 0.5 + fitted.top,
        width: fitted.width,
        height: fitted.height,
      };
      for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
        const p = this.logoScreenPoints[Math.min(i, this.logoScreenPoints.length - 1)]!;
        const screen = logoPointToScreenPoint(p, rect);
        cam.screenToWorld(screen.x, screen.y, SPAWN_DEPTH, this.scratch);
        this.invCam.transformPoint(this.scratch, this.scratch);
        const i3 = i * 3;
        this.formationStartsCam[i3] = this.scratch.x;
        this.formationStartsCam[i3 + 1] = this.scratch.y;
        this.formationStartsCam[i3 + 2] = this.scratch.z;
      }
    } else {
      const { cells, maxRing } = buildGridCells(canvasW, canvasH);
      this.maxRing = maxRing;
      for (let c = 0; c < GRID_CELL_COUNT; c++) {
        const cell = cells[Math.min(c, cells.length - 1)]!;
        cam.screenToWorld(cell.sx, canvasH - cell.sy, SPAWN_DEPTH, this.scratch);
        this.invCam.transformPoint(this.scratch, this.scratch);
        for (let d = 0; d < DOTS_PER_GRID_CELL; d++) {
          const i = c * DOTS_PER_GRID_CELL + d;
          const i3 = i * 3;
          this.formationStartsCam[i3] = this.scratch.x;
          this.formationStartsCam[i3 + 1] = this.scratch.y;
          this.formationStartsCam[i3 + 2] = this.scratch.z;
        }
      }

      const cellRings = new Uint16Array(GRID_CELL_COUNT);
      for (let c = 0; c < GRID_CELL_COUNT; c++) {
        cellRings[c] = cells[Math.min(c, cells.length - 1)]!.ring;
      }
      this.cellRingVisible = new Uint32Array(maxRing + 1);
      for (let ring = 0; ring <= maxRing; ring++) {
        let count = 0;
        for (let c = 0; c < GRID_CELL_COUNT; c++) {
          if (cellRings[c]! <= ring) count++;
        }
        this.cellRingVisible[ring] = count;
      }
    }
    this.ringIntervalMs = GRID_SPAWN_DURATION_MS / Math.max(1, this.maxRing + 1);

    const mesh = new pc.Mesh(device);
    mesh.setPositions(this.positions, 3);
    mesh.update(pc.PRIMITIVE_POINTS);
    this.mesh = mesh;
    this.syncDrawRange();

    const material = new pc.StandardMaterial();
    material.emissive = new pc.Color(1, 1, 1);
    material.emissiveIntensity = 1;
    material.useLighting = false;
    material.depthWrite = false;
    material.depthTest = false;
    material.cull = pc.CULLFACE_NONE;
    material.update();
    this.material = material;

    const meshInstance = new pc.MeshInstance(mesh, material);
    meshInstance.castShadow = false;
    meshInstance.pick = false;

    const entity = new pc.Entity('point-preview');
    entity.addComponent('render', {
      meshInstances: [meshInstance],
      castShadows: false,
      receiveShadows: false,
      layers: [pc.LAYERID_WORLD],
    });
    entity.setLocalPosition(0, 0, 0);
    entity.setLocalEulerAngles(0, 0, 0);
    cameraEntity.addChild(entity);
    this.entity = entity;
  }

  private syncDrawRange(): void {
    const prim = this.mesh.primitive[0]!;
    prim.type = pc.PRIMITIVE_POINTS;
    prim.base = this.drawBase;
    prim.count = this.drawCount;
  }

  private updateMeshPositions(): void {
    this.mesh.setPositions(this.positions, 3);
    this.mesh.update(pc.PRIMITIVE_POINTS);
    this.syncDrawRange();
  }

  beginGridSpawn(nowMs: number): void {
    if (this.phase !== 'hidden') return;
    this.phase = 'grid-spawn';
    this.lastRingMs = nowMs;
    this.spawnStartedMs = nowMs;
    this.currentRing = -1;
  }

  getPhase(): PointDotsPhase {
    return this.phase;
  }

  isGridComplete(): boolean {
    return this.phase === 'grid-hold' || this.phase === 'morphing' || this.phase === 'done';
  }

  isMorphDone(): boolean {
    return this.phase === 'done';
  }

  isWaitingForSplat(): boolean {
    return this.phase === 'grid-hold';
  }

  getSpawnProgress(): number {
    if (this.phase === 'hidden') return 0;
    if (this.isGridComplete()) return 1;
    if (this.logoScreenPoints) return Math.min(1, this.visibleDots / ENTRANCE_DOT_COUNT);
    return Math.max(0, this.visibleDots / ENTRANCE_DOT_COUNT);
  }

  getMorphProgress(): number {
    if (this.phase === 'done') return 1;
    if (this.phase !== 'morphing') return 0;
    return this.morphT;
  }

  tryBeginMorph(splatReady: boolean, pcvToWorld: PcvToWorldFn): boolean {
    if (!splatReady || this.phase !== 'grid-hold') return false;
    this.beginWorldMorph(pcvToWorld);
    this.phase = 'morphing';
    this.morphT = 0;
    return true;
  }

  /**
   * Provide the reveal wave origin (world space) so the preview points can be dissolved
   * radially — inner points convert to splats first, in lockstep with the expanding wave.
   * Must be called before/at morph; safe to call repeatedly.
   */
  setRevealCenterWorld(center: pc.Vec3): void {
    this.revealCenterWorld = center.clone();
  }

  /**
   * Hide every preview point whose silhouette radius is inside `waveRadius` of the reveal
   * centre. Points outside the wave stay lit, so the dots visibly become splats exactly
   * where the wave front is. Returns true once all points have dissolved.
   *
   * Cheap: dots were reordered inner→outer at morph time, so the live set is the single
   * contiguous tail `[dissolvedCount, ENTRANCE_DOT_COUNT)` — we only advance a cursor and
   * shrink the draw count, never resort or rewrite the whole buffer.
   */
  dissolveToRadius(waveRadius: number): boolean {
    if (this.phase !== 'done' && this.phase !== 'morphing') return false;
    if (!this.sortedRadii) return false;
    if (this.dissolvedCount >= ENTRANCE_DOT_COUNT) return true;

    let next = this.dissolvedCount;
    while (next < ENTRANCE_DOT_COUNT && this.sortedRadii[next]! <= waveRadius) next++;
    if (next === this.dissolvedCount) return false;

    this.dissolvedCount = next;
    const remaining = ENTRANCE_DOT_COUNT - next;
    // Draw only the outer (not-yet-revealed) tail of the position buffer.
    this.drawBase = next;
    this.drawCount = remaining;
    this.syncDrawRange();
    return remaining === 0;
  }

  isFullyDissolved(): boolean {
    return this.dissolvedCount >= ENTRANCE_DOT_COUNT;
  }

  getMaxRevealRadius(): number {
    if (!this.sortedRadii || this.sortedRadii.length === 0) return 0;
    return this.sortedRadii[this.sortedRadii.length - 1] ?? 0;
  }

  private beginWorldMorph(pcvToWorld: PcvToWorldFn): void {
    this.camMat.copy(this.cameraEntity.getWorldTransform());
    const startWorld = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      this.scratch.set(
        this.formationStartsCam[i3]!,
        this.formationStartsCam[i3 + 1]!,
        this.formationStartsCam[i3 + 2]!,
      );
      this.camMat.transformPoint(this.scratch, this.scratch);
      startWorld[i3] = this.scratch.x;
      startWorld[i3 + 1] = this.scratch.y;
      startWorld[i3 + 2] = this.scratch.z;
    }

    const worldTargets: pc.Vec3[] = [];
    const rankedTargets = [];
    const cameraPos = this.cameraEntity.getPosition();
    for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      this.scratch.set(this.pcvTargets[i3]!, this.pcvTargets[i3 + 1]!, this.pcvTargets[i3 + 2]!);
      pcvToWorld(this.scratch, this.scratch);
      const target = this.scratch.clone();
      worldTargets.push(target);
      this.cameraEntity
        .getWorldTransform()
        .transformPoint(this.formationStartsCamVec(i), this.scratch2);
      rankedTargets.push({
        x: target.x - this.scratch2.x,
        y: target.y,
        z: target.distance(cameraPos),
        target,
      });
    }

    const logoPoints =
      this.logoScreenPoints ??
      Array.from({ length: ENTRANCE_DOT_COUNT }, (_, i) => ({
        x: (i % DOTS_PER_GRID_CELL) / Math.max(1, DOTS_PER_GRID_CELL - 1),
        y: Math.floor(i / DOTS_PER_GRID_CELL) / Math.max(1, GRID_CELL_COUNT - 1),
      }));
    const assignedTargets = this.logoScreenPoints
      ? assignLogoTargetsToWorldTargets(logoPoints, rankedTargets).map((item) => item.target)
      : worldTargets;

    for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      const target = assignedTargets[i]!;
      this.morphStartWorld[i3] = startWorld[i3]!;
      this.morphStartWorld[i3 + 1] = startWorld[i3 + 1]!;
      this.morphStartWorld[i3 + 2] = startWorld[i3 + 2]!;
      this.morphEndWorld[i3] = target.x;
      this.morphEndWorld[i3 + 1] = target.y;
      this.morphEndWorld[i3 + 2] = target.z;
      this.positions[i3] = this.morphStartWorld[i3]!;
      this.positions[i3 + 1] = this.morphStartWorld[i3 + 1]!;
      this.positions[i3 + 2] = this.morphStartWorld[i3 + 2]!;
    }

    this.sceneRoot.addChild(this.entity);
    this.entity.setLocalPosition(0, 0, 0);
    this.entity.setLocalEulerAngles(0, 0, 0);
    this.entity.setLocalScale(1, 1, 1);

    this.drawBase = 0;
    this.drawCount = ENTRANCE_DOT_COUNT;
    this.updateMeshPositions();
  }

  private formationStartsCamVec(index: number): pc.Vec3 {
    const i3 = index * 3;
    return new pc.Vec3(
      this.formationStartsCam[i3]!,
      this.formationStartsCam[i3 + 1]!,
      this.formationStartsCam[i3 + 2]!,
    );
  }

  /**
   * Sort all dot lanes (start + end world positions) by each dot's final distance from the
   * reveal centre, ascending. Populates {@link sortedRadii} so {@link dissolveToRadius} can
   * cull inner dots first as the wave expands.
   */
  private reorderByRevealRadius(): void {
    const center = this.revealCenterWorld;
    const cx = center?.x ?? 0;
    const cy = center?.y ?? 0;
    const cz = center?.z ?? 0;

    const order = new Array<number>(ENTRANCE_DOT_COUNT);
    const radius = new Float32Array(ENTRANCE_DOT_COUNT);
    for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      const dx = this.morphEndWorld[i3]! - cx;
      const dy = this.morphEndWorld[i3 + 1]! - cy;
      const dz = this.morphEndWorld[i3 + 2]! - cz;
      radius[i] = Math.hypot(dx, dy, dz);
      order[i] = i;
    }
    order.sort((a, b) => radius[a]! - radius[b]!);

    const start = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    const end = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    const pos = new Float32Array(ENTRANCE_DOT_COUNT * 3);
    const sortedRadii = new Float32Array(ENTRANCE_DOT_COUNT);
    for (let dst = 0; dst < ENTRANCE_DOT_COUNT; dst++) {
      const src = order[dst]!;
      const s3 = src * 3;
      const d3 = dst * 3;
      start[d3] = this.morphStartWorld[s3]!;
      start[d3 + 1] = this.morphStartWorld[s3 + 1]!;
      start[d3 + 2] = this.morphStartWorld[s3 + 2]!;
      end[d3] = this.morphEndWorld[s3]!;
      end[d3 + 1] = this.morphEndWorld[s3 + 1]!;
      end[d3 + 2] = this.morphEndWorld[s3 + 2]!;
      pos[d3] = this.positions[s3]!;
      pos[d3 + 1] = this.positions[s3 + 1]!;
      pos[d3 + 2] = this.positions[s3 + 2]!;
      sortedRadii[dst] = radius[src]!;
    }
    this.morphStartWorld.set(start);
    this.morphEndWorld.set(end);
    this.positions.set(pos);
    this.sortedRadii = sortedRadii;
  }

  private revealThroughRing(ring: number): void {
    const nextCells = this.cellRingVisible[Math.min(ring, this.maxRing)] ?? GRID_CELL_COUNT;
    const nextDots = Math.min(ENTRANCE_DOT_COUNT, nextCells * DOTS_PER_GRID_CELL);
    if (nextDots <= this.visibleDots) return;
    for (let i = this.visibleDots; i < nextDots; i++) {
      const i3 = i * 3;
      this.positions[i3] = this.formationStartsCam[i3]!;
      this.positions[i3 + 1] = this.formationStartsCam[i3 + 1]!;
      this.positions[i3 + 2] = this.formationStartsCam[i3 + 2]!;
    }
    this.visibleDots = nextDots;
    this.drawBase = 0;
    this.drawCount = this.visibleDots;
    this.updateMeshPositions();
  }

  private revealLogoDots(nowMs: number): void {
    const t = Math.min(1, (nowMs - this.spawnStartedMs) / LOGO_SPAWN_DURATION_MS);
    const nextDots = Math.min(
      ENTRANCE_DOT_COUNT,
      Math.max(1, Math.floor(easeInOutCubic(t) * ENTRANCE_DOT_COUNT)),
    );
    for (let i = 0; i < nextDots; i++) {
      const i3 = i * 3;
      const logoPoint = this.logoScreenPoints?.[Math.min(i, this.logoScreenPoints.length - 1)];
      this.positions[i3] = this.formationStartsCam[i3]!;
      this.positions[i3 + 1] =
        this.formationStartsCam[i3 + 1]! +
        logoEntryOffsetY({
          elapsedMs: nowMs - this.spawnStartedMs,
          durationMs: LOGO_SPAWN_DURATION_MS,
          index: i,
          normalizedY: logoPoint?.y ?? 0,
        });
      this.positions[i3 + 2] = this.formationStartsCam[i3 + 2]!;
    }
    this.visibleDots = nextDots;
    this.drawBase = 0;
    this.drawCount = this.visibleDots;
    this.updateMeshPositions();
    if (t >= 1) this.phase = 'grid-hold';
  }

  private animateSceneHold(nowMs: number): void {
    if (!this.sortedRadii || this.dissolvedCount >= ENTRANCE_DOT_COUNT) return;
    if (this.sceneHoldStartedMs <= 0) this.sceneHoldStartedMs = nowMs;

    const center = this.revealCenterWorld;
    const cx = center?.x ?? 0;
    const cz = center?.z ?? 0;
    const elapsedSeconds = (nowMs - this.sceneHoldStartedMs) / 1000;

    for (let i = this.dissolvedCount; i < ENTRANCE_DOT_COUNT; i++) {
      const i3 = i * 3;
      const baseX = this.morphEndWorld[i3]!;
      const baseY = this.morphEndWorld[i3 + 1]!;
      const baseZ = this.morphEndWorld[i3 + 2]!;
      const dx = baseX - cx;
      const dz = baseZ - cz;
      const len = Math.hypot(dx, dz);
      const dirX = len > 1e-4 ? dx / len : 0;
      const dirZ = len > 1e-4 ? dz / len : 0;
      const offset = sceneWaitPulseOffset({
        elapsedSeconds,
        radius: this.sortedRadii[i]!,
        index: i,
        dirX,
        dirZ,
      });
      this.positions[i3] = baseX + offset.x;
      this.positions[i3 + 1] = baseY + offset.y;
      this.positions[i3 + 2] = baseZ + offset.z;
    }

    this.updateMeshPositions();
  }

  tick(nowMs: number, dt: number): void {
    if (this.phase === 'hidden') return;
    if (this.phase === 'done') {
      this.animateSceneHold(nowMs);
      return;
    }

    if (this.phase === 'grid-spawn') {
      if (this.logoScreenPoints) {
        this.revealLogoDots(nowMs);
        return;
      }
      if (this.currentRing < 0) {
        this.currentRing = 0;
        this.revealThroughRing(0);
      }
      while (this.currentRing < this.maxRing && nowMs - this.lastRingMs >= this.ringIntervalMs) {
        this.lastRingMs += this.ringIntervalMs;
        this.currentRing++;
        this.revealThroughRing(this.currentRing);
      }
      if (this.currentRing >= this.maxRing && this.visibleDots >= ENTRANCE_DOT_COUNT) {
        this.phase = 'grid-hold';
      }
      return;
    }

    if (this.phase === 'morphing') {
      this.morphT = Math.min(1, this.morphT + dt / MORPH_DURATION_S);
      const e = easeInOutCubic(this.morphT);
      for (let i = 0; i < ENTRANCE_DOT_COUNT; i++) {
        const i3 = i * 3;
        this.positions[i3] =
          this.morphStartWorld[i3]! + (this.morphEndWorld[i3]! - this.morphStartWorld[i3]!) * e;
        this.positions[i3 + 1] =
          this.morphStartWorld[i3 + 1]! +
          (this.morphEndWorld[i3 + 1]! - this.morphStartWorld[i3 + 1]!) * e;
        this.positions[i3 + 2] =
          this.morphStartWorld[i3 + 2]! +
          (this.morphEndWorld[i3 + 2]! - this.morphStartWorld[i3 + 2]!) * e;
      }
      this.updateMeshPositions();
      if (this.morphT >= 1) {
        this.phase = 'done';
        this.reorderByRevealRadius();
        this.sceneHoldStartedMs = nowMs;
        this.updateMeshPositions();
      }
    }
  }

  dispose(): void {
    this.entity.destroy();
    this.mesh.destroy();
    this.material.destroy();
  }
}

export { GRID_SPACING_PX, GRID_SPAWN_DURATION_MS, MORPH_DURATION_S };
